import type { SubscriptionToken } from '../types/subscription-token.ts'
import { Globals } from '../globals'
import { isEqual } from '../utils/hash'
import { getUuidV7 } from '../utils/uuid.ts'
import { buildTrackedEventMeta, type TrackedEventMeta } from './tracked-event-meta'
import { getCleanObject } from '../utils/get-clean-object'
import { isWriteAheadAction, TrackedEventAction } from './tracked-event-action'
import { PushSdkConfig } from '../../../sdk/src/types/push-sdk-config.ts'
import { parseErrorStack } from '../utils/parse-error-stack.ts'
import { PNException } from '../exceptions/pn-exception.ts'
import { snakeToPascalCase } from '../utils/case-convert.ts'
import { getSorted } from '../utils/get-sorted.ts'

/** @internal */
export type TrackedEvent = {
    sendAttemptCount: number
    jxms?: number

    readonly event_id: string
    /**
     * Per-tab session join key (uuidv7) stamped from {@link TrackedEventUtils._sessionId} at build
     * (backfilled in {@link TrackedEventUtils._ensureLateInitValues}). Correlates every event within
     * a single visit — impression, prompt/permission outcomes, `sdk_impression_end`. Never gated; a null
     * value is stripped on the wire.
     */
    session_id: string | null
    /**
     * Client occurrence time (epoch ms), stamped once at `track()` / {@link TrackedEventUtils._build}.
     * Immutable: never re-stamped on requeue, IDB hydration, or resend. This is the ordering clock —
     * the backend skew-corrects it (`received_at - (sent_at - occurred_at)`) and the identity guard
     * orders on it. See ADR "Client Occurrence-Time Ordering for Identity Writes (Envelope v1)".
     */
    readonly occurred_at: number
    /**
     * Per-installation monotonic counter, incremented once per event built in this context.
     * Deterministic tiebreak when two events share an `occurred_at` (and clock-independent intra-context
     * ordering). Immutable per event. NOTE (v1): in-memory per context (page / SW realm); global
     * cross-context monotonicity arrives with the Web Locks single-writer work.
     */
    readonly seq: number
    /**
     * Client send time (epoch ms), stamped at every send attempt in {@link TrackedEventUtils._prepareSendEvent}.
     * Mutable by design: it reflects the actual flush of *this* attempt so the backend can estimate
     * clock skew. Unset until the first send.
     */
    sent_at?: number
    readonly channel: string
    provider: string
    readonly action: TrackedEventAction
    domain_id: number | null
    domain_key?: string | null
    pushly_unique_user_id: string | null
    token: SubscriptionToken | null
    readonly meta: TrackedEventMeta
    data: Record<string, any>
}

/** @internal */
export class TrackedEventUtils {
    // region Late-init Global Properties
    public static _domainKey: string | null
    public static _domainId: number | null
    public static _anonymousId: string | null
    public static _loadConfig: PushSdkConfig | null
    public static _token: SubscriptionToken | null
    /** Per-tab session join key (uuidv7). Set before the first event is built; null in the SW realm. */
    public static _sessionId: string | null
    /** Per-installation monotonic event counter (see TrackedEvent.seq). In-memory per realm. */
    public static _seq: number
    // endregion

    static {
        this._domainKey = null
        this._domainId = null
        this._anonymousId = null
        this._loadConfig = null
        this._token = null
        this._sessionId = null
        this._seq = 0
    }

    public static async _build(
        action: TrackedEventAction,
        data?: Record<string, any>,
        jxms?: number,
    ): Promise<TrackedEvent> {
        const event = getCleanObject<TrackedEvent>({
            sendAttemptCount: 0,
            event_id: getUuidV7(),
            session_id: this._sessionId,
            occurred_at: Date.now(),
            seq: ++this._seq,
            channel: Globals._tevChannel,
            provider: Globals._tevVapidProvider,
            action,
            domain_id: this._domainId,
            domain_key: this._domainKey,
            pushly_unique_user_id: this._anonymousId,
            token: this._token,
            meta: buildTrackedEventMeta(),
            data: data ?? {},
        })

        if (jxms) {
            event.jxms = jxms
        }

        if (this._loadConfig?.externalId && !event.data[TrackedEventAction.EXTERNAL_ID]) {
            event.data[TrackedEventAction.EXTERNAL_ID] = this._loadConfig?.externalId
        }

        return event
    }

    public static async _getErrorEvent(error: Error, data?: Record<string, any>): Promise<TrackedEvent> {
        const stackEntries =
            error instanceof PNException
                ? error._stack
                : parseErrorStack(error.stack, [PNException._filterBuilderStackEntry])

        const backtrace = stackEntries.length
            ? stackEntries.map((e) => `${e.function ?? '<anonymous>'} (${e.file}:${e.line}:${e.column})`)
            : undefined

        return this._build(TrackedEventAction.ERROR, {
            [Globals._tevFieldErrorType]: error instanceof PNException ? snakeToPascalCase(error.code) : error.name,
            [Globals._tevFieldErrorMessage]: error instanceof PNException ? error._compositeMessage : error.message,
            ...(backtrace ? { [Globals._tevFieldErrorBacktrace]: backtrace } : {}),
            ...(data ?? {}),
        })
    }

    public static async _getProfileExternalIdEvent(id: string): Promise<TrackedEvent> {
        return this._build(TrackedEventAction.PROFILE, { external_id: id })
    }

    public static async _getProfileLanguageEvent(lang: string): Promise<TrackedEvent> {
        return this._build(TrackedEventAction.PROFILE, { language: lang })
    }

    public static async _getProfileTimeZoneEvent(timeZone: string): Promise<TrackedEvent> {
        return this._build(TrackedEventAction.PROFILE, { time_zone: timeZone })
    }

    /**
     * Asserts equality between two `TrackedEvent` items
     * using [`getEventHashParts()`]{@link _getEventHashParts} and [`isEqual()`]{@link isEqual}
     *
     * @internal
     */
    public static _isEqual(a: TrackedEvent, b: TrackedEvent): boolean {
        const aHashParts = this._getEventHashParts(a)
        const bHashParts = this._getEventHashParts(b)
        return isEqual(aHashParts, bHashParts)
    }

    /**
     * @mutates event
     * @internal
     */
    public static _prepareSendEvent(event: TrackedEvent): void {
        event.sendAttemptCount += 1
        // Stamp the actual send time of THIS attempt (re-stamped on each retry). occurred_at/seq/event_id
        // are deliberately left untouched so the occurrence clock is immutable across requeues.
        event.sent_at = Date.now()
        this._ensureLateInitValues(event)
    }

    /**
     * Examines `TrackedEvent` properties that contain values
     * derived from storage or late-init assignment. If the values
     * are not present they will be loaded and assigned.
     *
     * @mutates event
     * @internal
     */
    protected static _ensureLateInitValues(event: TrackedEvent): void {
        if (!event.pushly_unique_user_id && this._anonymousId) {
            event.pushly_unique_user_id = this._anonymousId
        }

        if (event.session_id == null && this._sessionId != null) {
            event.session_id = this._sessionId
        }

        if (!event.domain_id && this._domainId) {
            event.domain_id = this._domainId
        }
        if (!event.domain_id && !event.domain_key && this._domainKey) {
            event.domain_key = this._domainKey
        }
        if (event.domain_id && event.domain_key) {
            delete event.domain_key
        }

        /*
         token value should only be updated if not previously set.
         Events with different tokens, as in the case of a token update,
         should keep their original values.

         A write-ahead event is never filled: a withdrawal or deletion names the subscription it was
         made against, and a stored one delivered after the visitor re-subscribed must not carry,
         and so unsubscribe, the newer one.
         */
        if (!event.token && this._token && !isWriteAheadAction(event.action)) {
            event.token = this._token
        }
    }

    /** @internal */
    public static _clone<T extends TrackedEvent>(event: T): T {
        let clone: T | undefined = undefined
        if ('structuredClone' in self) {
            try {
                clone = structuredClone(event)
            } catch {}
        }

        if (!clone) {
            const { sendAttemptCount, event_id, data, ...props } = event

            clone = getCleanObject({
                ...props,
            }) as T
        }

        // A clone is a fresh event (new id, reset attempts) — give it its own occurrence stamps too,
        // so it never inherits the source's immutable occurred_at/seq or a stale sent_at.
        ;(clone as any).sendAttemptCount = 0
        ;(clone as any).event_id = getUuidV7()
        ;(clone as any).occurred_at = Date.now()
        ;(clone as any).seq = ++this._seq
        ;(clone as any).sent_at = undefined
        clone.data = getCleanObject(event.data)

        return clone!
    }

    /**
     * Returns an ordered dict representing the unique
     * properties within a `TrackedEvent`.
     */
    protected static _getEventHashParts(event: TrackedEvent) {
        return getCleanObject({
            a: event.action,
            di: event.domain_id ?? 'unknown',
            dk: event.domain_key ?? 'unknown',
            puuid: event.pushly_unique_user_id,
            t: event.token ? Object.fromEntries(Object.keys(event.token).sort().entries()) : 'none',
            d: getSorted(event.data),
        })
    }
}
